import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Dropdown } from 'react-bootstrap'
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'

import { useProject } from '@/Hooks/UseProject'
import useTranslation from '@/Hooks/UseTranslation'
import ProjectLayout from '@/Layouts/MainLayout/ProjectLayout'
import ContentBody from '@/Layouts/MainLayout/components/ContentBody'
import ProjectOverviewHeader from '@/Pages/App/Project/Home/Components/ProjectOverviewHeader'
import { LeanDrilldownRequest, loadLeanDrilldown, loadLeanIndicators } from '@/Services/LeanIndicatorsService'
import { loadTasks as loadLegacyPpcTasks, loadWeeklyProgress } from '@/Services/IndicatorsPPCService'
import { normalizeLegacyPpcWeeks } from '@/Helpers/LegacyPpcHelper'
import PpcMethodNotice from '@/Components/PpcMethodNotice/PpcMethodNotice'
import { leanIndicatorsDemoData } from './LeanIndicatorsDemoData'

import './LeanIndicatorsDashboardPage.scss'

type Section = 'summary' | 'ppc' | 'pcr' | 'cnc' | 'quality'

const sections: Section[] = [ 'summary', 'ppc', 'pcr', 'cnc', 'quality' ]
const colors = [ '#ff6720', '#663399', '#147d64', '#2764c5', '#c34832', '#64748b' ]

const formatPercent = (value: number | null | undefined): string => value == null ? '—' : `${value.toFixed(1)}%`
const periodLabel = (period: any): string => typeof period === 'string' ? period : period?.key || '—'

const MetricCard = ({ label, value, detail, tone = 'orange', onClick }: any) => (
  <article className={`lean-metric-card lean-metric-card--${tone} ${onClick ? 'lean-metric-card--interactive' : ''}`} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={(event) => { if (onClick && (event.key === 'Enter' || event.key === ' ')) onClick() }}>
    <span>{label}</span>
    <strong>{formatPercent(value)}</strong>
    <small>{detail}</small>
  </article>
)

const EmptyState = ({ text }: { text: string }) => <div className="lean-empty">{text}</div>

interface LeanFilterDropdownProps {
  id: string
  label: string
  value: string
  options: Array<{ key: string, label?: string }>
  allLabel: string
  onChange: (value: string) => void
}

const LeanFilterDropdown = ({
  id,
  label,
  value,
  options,
  allLabel,
  onChange,
}: LeanFilterDropdownProps) => {
  const selected = options.find((option) => option.key === value)

  return (
    <div className="lean-filter-field">
      <span id={`${id}-label`} className="lean-filter-field__label">{label}</span>
      <Dropdown className="lean-filter-dropdown">
        <Dropdown.Toggle
          as="button"
          type="button"
          className="lean-filter-dropdown__toggle"
          aria-labelledby={`${id}-label ${id}-value`}
        >
          <span id={`${id}-value`} className="lean-filter-dropdown__value">
            {selected?.label || selected?.key || allLabel}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="pl4-programmation-create-menu__menu lean-filter-dropdown__menu">
          {[ { key: '', label: allLabel }, ...options ].map((option) => {
            const active = option.key === value
            return (
              <Dropdown.Item
                as="button"
                key={option.key || 'all'}
                active={active}
                className="pl4-programmation-create-menu__item lean-filter-dropdown__item"
                onClick={() => onChange(option.key)}
              >
                <span className="lean-filter-dropdown__check" aria-hidden="true">
                  {active && <i className="fas fa-check" />}
                </span>
                <span>{option.label || option.key}</span>
              </Dropdown.Item>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

const LeanIndicatorsDashboardPage = () => {
  const { t } = useTranslation()
  const { project_id, projectLoaded } = useProject()
  const location = useLocation()
  const history = useHistory()
  const [ data, setData ] = useState<any | null>(null)
  const [ legacyPpcWeeks, setLegacyPpcWeeks ] = useState<any[]>([])
  const [ legacyPpcError, setLegacyPpcError ] = useState('')
  const [ metricErrors, setMetricErrors ] = useState<Partial<Record<string, true>>>({})
  const [ error, setError ] = useState('')
  const [ loading, setLoading ] = useState(false)
  const [ drilldown, setDrilldown ] = useState<any | null>(null)
  const [ drilldownLoading, setDrilldownLoading ] = useState(false)
  const [ drilldownError, setDrilldownError ] = useState('')
  const [ from, setFrom ] = useState('')
  const [ to, setTo ] = useState('')
  const [ dimensionFilters, setDimensionFilters ] = useState({ performer: '', group: '', taskType: '', location: '', family: '', cause: '' })
  const isDemo = location.pathname === '/preview/lean-indicators'

  const querySection = new URLSearchParams(location.search).get('section') as Section | null
  const section: Section = querySection && sections.includes(querySection) ? querySection : 'summary'

  const refresh = async (
    appliedFrom = from,
    appliedTo = to,
    appliedDimensionFilters = dimensionFilters,
  ) => {
    if (isDemo) {
      setData(leanIndicatorsDemoData)
      setLegacyPpcWeeks(leanIndicatorsDemoData.legacyPpc.weeks)
      setLegacyPpcError('')
      setMetricErrors({})
      return
    }
    if (!project_id) return
    setLoading(true)
    setError('')
    setLegacyPpcError('')
    try {
      const [ leanResult, legacyResult ] = await Promise.all([
        loadLeanIndicators({
          projectId: project_id,
          from: appliedFrom || undefined,
          to: appliedTo || undefined,
          performerIds: appliedDimensionFilters.performer ? [ appliedDimensionFilters.performer ] : undefined,
          groupIds: appliedDimensionFilters.group ? [ appliedDimensionFilters.group ] : undefined,
          taskTypes: appliedDimensionFilters.taskType ? [ appliedDimensionFilters.taskType ] : undefined,
          locations: appliedDimensionFilters.location ? [ appliedDimensionFilters.location ] : undefined,
          restrictionFamilies: appliedDimensionFilters.family ? [ appliedDimensionFilters.family ] : undefined,
          cncCategoryIds: appliedDimensionFilters.cause ? [ appliedDimensionFilters.cause ] : undefined,
        }).then((value) => ({ value })).catch(() => ({ error: true as const })),
        loadWeeklyProgress(project_id).then((value) => ({ value })).catch(() => ({ error: true as const })),
      ])
      if ('value' in leanResult) {
        setData(leanResult.value)
        setMetricErrors(leanResult.value.errors || {})
        if (Object.keys(leanResult.value.errors || {}).length === 4) setError(t('LEAN_INDICATORS:LoadError'))
      } else {
        setData({ ppc: null, pcr: null, cnc: null, quality: [] })
        setMetricErrors({ ppc: true, pcr: true, cnc: true, quality: true })
        setError(t('LEAN_INDICATORS:LoadError'))
      }
      if ('error' in legacyResult || legacyResult.value.error || !('data' in legacyResult.value)) {
        setLegacyPpcWeeks([])
        setLegacyPpcError(t('LEAN_INDICATORS:LegacyPpcLoadError'))
      } else {
        setLegacyPpcWeeks(legacyResult.value.data?.data?.weeks || [])
      }
    } catch (requestError) {
      setError(t('LEAN_INDICATORS:LoadError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isDemo || projectLoaded) refresh()
  }, [ projectLoaded, project_id, isDemo ])

  const selectSection = (next: Section) => history.replace({
    pathname: location.pathname,
    search: next === 'summary' ? '' : `?section=${next}`,
  })

  const ppcRows = data?.ppc?.series || []
  const legacyPpcRows = useMemo(
    () => normalizeLegacyPpcWeeks(legacyPpcWeeks, from || undefined, to || undefined),
    [ legacyPpcWeeks, from, to ],
  )
  const pcrRows = data?.pcr?.series || []
  const trend = useMemo(() => {
    const rows = new Map<string, any>()
    ppcRows.forEach((row: any) => rows.set(periodLabel(row.period), {
      period: periodLabel(row.period), ppc: row.ppc.value, movingAverage4: row.movingAverage4,
    }))
    pcrRows.forEach((row: any) => {
      const key = periodLabel(row.period)
      const current = rows.get(key) || { period: key }
      row.horizons.forEach((metric: any) => { current[`pcr${metric.horizonWeeks}`] = metric.value })
      rows.set(key, current)
    })
    return Array.from(rows.values()).sort((a, b) => a.period.localeCompare(b.period))
  }, [ ppcRows, pcrRows ])

  const latestPcr = data?.pcr?.latest || []
  const failures = data?.cnc?.coverage?.denominator || 0
  const quality = data?.quality || []
  const filterOptions = {
    performer: data?.ppc?.breakdowns?.performer || [],
    group: data?.ppc?.breakdowns?.group || [],
    taskType: data?.ppc?.breakdowns?.taskType || [],
    location: data?.ppc?.breakdowns?.location || [],
    family: data?.pcr?.breakdowns?.family || [],
    cause: (data?.cnc?.pareto || []).map((row: any) => ({
      key: row.categoryId || row.code || 'UNASSIGNED',
      label: row.label,
    })),
  }

  const updateDimensionFilter = (key: string, value: string) => setDimensionFilters((current) => ({ ...current, [key]: value }))
  const clearFilters = () => {
    const emptyDimensionFilters = { performer: '', group: '', taskType: '', location: '', family: '', cause: '' }
    setFrom('')
    setTo('')
    setDimensionFilters(emptyDimensionFilters)
    refresh('', '', emptyDimensionFilters)
  }

  const demoDrilldown = (request: LeanDrilldownRequest): any => {
    let records: any[] = []
    if (request.metric === 'ppc') records = ppcRows.map((row: any, index: number) => ({ id: `demo-ppc-${index}`, periodKey: periodLabel(row.period), taskName: `Compromiso semanal ${index + 1}`, promisedDate: `2026-09-${10 + index}`, result: index % 4 === 0 ? 'not_kept' : 'kept' }))
    if (request.metric === 'pcr') records = [ 'ready', 'ready', 'at_risk', 'blocked', 'ready' ].map((state, index) => ({ id: `demo-pcr-${index}`, periodKey: '2026-W36', horizonWeeks: request.horizonWeeks || 1, taskName: `Actividad lookahead ${index + 1}`, state, openRestrictions: state === 'ready' ? 0 : 1 }))
    if (request.metric === 'cnc') records = (data?.cnc?.pareto || []).filter((row: any) => !request.bucket || row.code === request.bucket).map((row: any, index: number) => ({ id: `demo-cnc-${index}`, periodKey: '2026-W36', taskName: `Actividad incumplida ${index + 1}`, causeCode: row.code, cause: row.label, status: row.code === 'UNASSIGNED' ? 'pending_analysis' : 'analyzed' }))
    if (request.metric === 'quality') records = [ { id: 'demo-quality-1', taskName: 'Actividad demostrativa', issue: request.bucket, status: 'requiere corrección' } ]
    const denominatorCount = records.length
    const numeratorCount = request.metric === 'ppc' ? records.filter((row) => row.result === 'kept').length : request.metric === 'pcr' ? records.filter((row) => row.state === 'ready').length : records.filter((row) => row.status === 'analyzed').length
    return { records, nextCursor: null, totalCount: records.length, reconciliation: { metric: request.metric, numeratorCount, denominatorCount, returnedCount: records.length, matches: true } }
  }

  const openDrilldown = async (request: Omit<LeanDrilldownRequest, 'projectId'>, title: string) => {
    setDrilldown({ title, records: [], reconciliation: null })
    setDrilldownLoading(true)
    setDrilldownError('')
    try {
      const appliedRequest = {
        ...request,
        projectId: isDemo ? 'demo' : project_id,
        performerIds: dimensionFilters.performer ? [ dimensionFilters.performer ] : undefined,
        groupIds: dimensionFilters.group ? [ dimensionFilters.group ] : undefined,
        taskTypes: dimensionFilters.taskType ? [ dimensionFilters.taskType ] : undefined,
        locations: dimensionFilters.location ? [ dimensionFilters.location ] : undefined,
        restrictionFamilies: dimensionFilters.family ? [ dimensionFilters.family ] : undefined,
        cncCategoryIds: dimensionFilters.cause ? [ dimensionFilters.cause ] : undefined,
      }
      const result = isDemo ? demoDrilldown(appliedRequest) : await loadLeanDrilldown(appliedRequest)
      setDrilldown({ ...result, title })
    } catch (requestError) {
      setDrilldownError(t('LEAN_INDICATORS:DrilldownError'))
    } finally {
      setDrilldownLoading(false)
    }
  }

  const openLegacyPpcDrilldown = async (row: any) => {
    const title = `${t('LEAN_INDICATORS:PPCLegacyName')} · ${row.periodKey}`
    setDrilldown({ title, records: [], reconciliation: null })
    setDrilldownLoading(true)
    setDrilldownError('')
    try {
      let records: any[]
      if (isDemo) {
        records = [
          { id: 'legacy-demo-1', taskName: 'Actividad programada 1', taskType: 'trabajo', result: 'completed' },
          { id: 'legacy-demo-2', taskName: 'Actividad programada 2', taskType: 'partida', result: 'not_completed' },
        ]
      } else {
        const response = await loadLegacyPpcTasks(project_id, `${row.longNumber}`)
        if (response.error) throw new Error(response.error)
        const payload = response.data?.data || response.data || {}
        records = [
          ...(payload.completed || []).map((item: any) => ({
            id: item._id,
            taskName: item.task?.name || '—',
            taskType: item.taskType || item.task?.type || '—',
            result: 'completed',
          })),
          ...(payload.notCompleted || []).map((item: any) => ({
            id: item._id,
            taskName: item.task?.name || '—',
            taskType: item.taskType || item.task?.type || '—',
            result: 'not_completed',
          })),
        ]
      }
      setDrilldown({ title, records, reconciliation: null, totalCount: records.length })
    } catch (requestError) {
      setDrilldownError(t('LEAN_INDICATORS:DrilldownError'))
    } finally {
      setDrilldownLoading(false)
    }
  }

  const renderDrilldown = () => {
    if (!drilldown) return null
    const records = drilldown.records || []
    const columns = records.length ? Object.keys(records[0]).filter((key) => ![ 'id', 'snapshotId', 'conditionsOfSatisfaction' ].includes(key)) : []
    return <div className="lean-drawer-backdrop" onClick={() => setDrilldown(null)}><aside className="lean-drawer" role="dialog" aria-modal="true" aria-label={drilldown.title} onClick={(event) => event.stopPropagation()}>
      <header><div><span>{t('LEAN_INDICATORS:Traceability')}</span><h2>{drilldown.title}</h2></div><button onClick={() => setDrilldown(null)} aria-label={t('LEAN_INDICATORS:Close')}>×</button></header>
      {drilldownLoading && <div className="lean-loading">{t('LEAN_INDICATORS:LoadingDetail')}</div>}
      {drilldownError && <div className="lean-error">{drilldownError}</div>}
      {!drilldownLoading && !drilldownError && <>
        {drilldown.reconciliation && <div className={`lean-reconciliation ${drilldown.reconciliation.matches ? 'matches' : 'mismatch'}`}><div><span>{t('LEAN_INDICATORS:Numerator')}</span><strong>{drilldown.reconciliation.numeratorCount}</strong></div><div><span>{t('LEAN_INDICATORS:Denominator')}</span><strong>{drilldown.reconciliation.denominatorCount}</strong></div><div><span>{t('LEAN_INDICATORS:Records')}</span><strong>{drilldown.totalCount}</strong></div><b>{drilldown.reconciliation.matches ? t('LEAN_INDICATORS:Reconciled') : t('LEAN_INDICATORS:Mismatch')}</b></div>}
        {!records.length ? <EmptyState text={t('LEAN_INDICATORS:NoDetail')} /> : <div className="lean-drawer-table"><table><thead><tr>{columns.map((column) => <th key={column}>{column.replace(/([A-Z])/g, ' $1')}</th>)}</tr></thead><tbody>{records.map((record: any) => <tr key={record.id}>{columns.map((column) => <td key={column}>{Array.isArray(record[column]) ? record[column].length : record[column] == null ? '—' : `${record[column]}`}</td>)}</tr>)}</tbody></table></div>}
      </>}
    </aside></div>
  }

  const renderSummary = () => (
    <>
      <div className="lean-metric-grid">
        <MetricCard label={t('LEAN_INDICATORS:PPCLeanName')} value={data?.ppc?.latestClosed?.value} tone="orange" onClick={() => openDrilldown({ metric: 'ppc' }, t('LEAN_INDICATORS:PPCLeanName'))}
          detail={`${data?.ppc?.latestClosed?.numerator || 0}/${data?.ppc?.latestClosed?.denominator || 0} ${t('LEAN_INDICATORS:PromisesKept')}`} />
        <MetricCard label="PCR · 1" value={latestPcr.find((row: any) => row.horizonWeeks === 1)?.value} tone="purple" onClick={() => openDrilldown({ metric: 'pcr', horizonWeeks: 1 }, 'PCR · 1')}
          detail={t('LEAN_INDICATORS:ReadyOneWeek')} />
        <MetricCard label="PCR · 2" value={latestPcr.find((row: any) => row.horizonWeeks === 2)?.value} tone="green" onClick={() => openDrilldown({ metric: 'pcr', horizonWeeks: 2 }, 'PCR · 2')}
          detail={t('LEAN_INDICATORS:ReadyTwoWeeks')} />
        <MetricCard label={t('LEAN_INDICATORS:CNCCoverage')} value={data?.cnc?.coverage?.value} tone="blue" onClick={() => openDrilldown({ metric: 'cnc' }, t('LEAN_INDICATORS:CNCCoverage'))}
          detail={`${data?.cnc?.coverage?.numerator || 0}/${failures} ${t('LEAN_INDICATORS:FailuresAnalyzed')}`} />
      </div>
      <section className="lean-panel lean-panel--chart">
        <div className="lean-panel__heading"><h2>{t('LEAN_INDICATORS:ReliabilityTrend')}</h2><span>{t('LEAN_INDICATORS:ClosedPeriods')}</span></div>
        {!trend.length ? <EmptyState text={t('LEAN_INDICATORS:NoData')} /> : (
          <ResponsiveContainer width="100%" height={330}>
            <ComposedChart data={trend} margin={{ top: 16, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" /><YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => formatPercent(Number(v))} /><Legend />
              <Line dataKey="ppc" name={t('LEAN_INDICATORS:PPCLeanName')} stroke="#ff6720" strokeWidth={3} connectNulls />
              <Line dataKey="pcr1" name="PCR · 1" stroke="#663399" strokeWidth={2} connectNulls />
              <Line dataKey="pcr2" name="PCR · 2" stroke="#147d64" strokeWidth={2} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </section>
    </>
  )

  const renderPpc = () => {
    const latestLegacy = legacyPpcRows.length ? legacyPpcRows[legacyPpcRows.length - 1] : null
    const formatLegacyPercent = (value: number | null | undefined): string => (
      value == null ? `— · ${t('LEAN_INDICATORS:NoDataShort')}` : formatPercent(value)
    )

    return <>
      <PpcMethodNotice />
      <section className="lean-panel">
        <div className="lean-panel__heading"><div><h2>{t('LEAN_INDICATORS:PPCTitle')}</h2><p>{t('LEAN_INDICATORS:PPCDefinition')}</p></div><strong>{formatPercent(data?.ppc?.cumulative?.value)}</strong></div>
        {metricErrors.ppc ? <div className="lean-error" role="alert"><span>{t('LEAN_INDICATORS:LeanPpcLoadError')}</span><button onClick={() => refresh()}>{t('LEAN_INDICATORS:Retry')}</button></div> : !ppcRows.length ? <EmptyState text={t('LEAN_INDICATORS:NoClosedWWP')} /> : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={ppcRows.map((row: any) => ({ ...row, periodKey: periodLabel(row.period), value: row.ppc.value }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="periodKey" /><YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: any) => formatPercent(Number(v))} /><Legend />
                <Bar dataKey="value" name={t('LEAN_INDICATORS:PPCLeanName')} fill="#ff6720" radius={[4, 4, 0, 0]} />
                <Line dataKey="movingAverage4" name={t('LEAN_INDICATORS:MovingAverage')} stroke="#663399" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="lean-table-wrap"><table><thead><tr><th>{t('LEAN_INDICATORS:Period')}</th><th>PPC</th><th>{t('LEAN_INDICATORS:Kept')}</th><th>{t('LEAN_INDICATORS:NotKept')}</th><th>{t('LEAN_INDICATORS:Withdrawn')}</th></tr></thead>
              <tbody>{ppcRows.slice().reverse().map((row: any) => <tr key={periodLabel(row.period)} onClick={() => openDrilldown({ metric: 'ppc', periodKey: periodLabel(row.period) }, `PPC · ${periodLabel(row.period)}`)}><td>{periodLabel(row.period)} {row.retrospective && <span className="lean-period-badge" title={row.retrospectiveReason || ''}>{t('WEEKLY_PLAN:RetrospectiveBadge')}</span>}</td><td>{formatPercent(row.ppc.value)}</td><td>{row.kept}</td><td>{row.notKept}</td><td>{row.withdrawn}</td></tr>)}</tbody></table></div>
          </>
        )}
      </section>

      <section className="lean-panel lean-panel--legacy-ppc">
        <div className="lean-panel__heading">
          <div><h2>{t('LEAN_INDICATORS:PPCLegacyName')}</h2><p>{t('LEAN_INDICATORS:PPCLegacyDefinition')}</p></div>
          <span className="lean-method-badge">{t('LEAN_INDICATORS:LegacyMethod')}</span>
        </div>
        <p className="lean-filter-scope">{t('LEAN_INDICATORS:LegacyFilterScope')}</p>
        {legacyPpcError ? <div className="lean-error" role="alert"><span>{legacyPpcError}</span><button onClick={() => refresh()}>{t('LEAN_INDICATORS:Retry')}</button></div> : !legacyPpcRows.length ? <EmptyState text={t('LEAN_INDICATORS:LegacyPpcNoData')} /> : (
          <>
            <div className="lean-legacy-metrics">
              <div><span>{t('LEAN_INDICATORS:LegacyItems')}</span><strong>{formatLegacyPercent(latestLegacy?.percentItems)}</strong><small>{t('LEAN_INDICATORS:LatestClosedPeriod')} · {latestLegacy?.periodKey}</small></div>
              <div><span>{t('LEAN_INDICATORS:LegacyWorks')}</span><strong>{formatLegacyPercent(latestLegacy?.percentWorks)}</strong><small>{t('LEAN_INDICATORS:LatestClosedPeriod')} · {latestLegacy?.periodKey}</small></div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={legacyPpcRows}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="periodKey" /><YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: any) => formatLegacyPercent(v == null ? null : Number(v))} /><Legend />
                <Line dataKey="percentItems" name={t('LEAN_INDICATORS:LegacyItems')} stroke="#2764c5" strokeWidth={2} />
                <Line dataKey="percentWorks" name={t('LEAN_INDICATORS:LegacyWorks')} stroke="#64748b" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="lean-table-wrap"><table><thead><tr><th>{t('LEAN_INDICATORS:Period')}</th><th>{t('LEAN_INDICATORS:ProjectWeek')}</th><th>{t('LEAN_INDICATORS:LegacyItems')}</th><th>{t('LEAN_INDICATORS:LegacyWorks')}</th></tr></thead>
              <tbody>{legacyPpcRows.slice().reverse().map((row: any) => <tr key={row.periodKey} onClick={() => openLegacyPpcDrilldown(row)}><td>{row.periodKey}</td><td>{row.projectWeek}</td><td>{formatLegacyPercent(row.percentItems)}</td><td>{formatLegacyPercent(row.percentWorks)}</td></tr>)}</tbody></table></div>
          </>
        )}
      </section>
    </>
  }

  const renderPcr = () => (
    <>
      <div className="lean-metric-grid lean-metric-grid--three">
        {latestPcr.map((row: any) => <MetricCard key={row.horizonWeeks} label={`PCR · ${row.horizonWeeks}`} value={row.value} tone={row.horizonWeeks === 1 ? 'purple' : 'green'} detail={`${row.numerator}/${row.denominator} ${t('LEAN_INDICATORS:ReadyActivities')}`} />)}
        <MetricCard label={t('LEAN_INDICATORS:ReleaseCompliance')} value={data?.pcr?.releaseCompliance?.value} tone="blue" detail={`${data?.pcr?.releaseCompliance?.numerator || 0}/${data?.pcr?.releaseCompliance?.denominator || 0}`} />
      </div>
      <div className="lean-two-columns">
        <section className="lean-panel"><div className="lean-panel__heading"><h2>{t('LEAN_INDICATORS:RestrictionFlow')}</h2></div>
          {!data?.pcr?.restrictionFlow?.length ? <EmptyState text={t('LEAN_INDICATORS:NoData')} /> : <ResponsiveContainer width="100%" height={280}><BarChart data={data.pcr.restrictionFlow.map((row: any) => ({ ...row, periodKey: periodLabel(row.period) }))}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="periodKey" /><YAxis /><Tooltip /><Legend /><Bar dataKey="identified" name={t('LEAN_INDICATORS:Identified')} fill="#94a3b8" /><Bar dataKey="closedOnTime" name={t('LEAN_INDICATORS:ClosedOnTime')} fill="#147d64" /><Bar dataKey="closedLate" name={t('LEAN_INDICATORS:ClosedLate')} fill="#c34832" /></BarChart></ResponsiveContainer>}
        </section>
        <section className="lean-panel"><div className="lean-panel__heading"><h2>{t('LEAN_INDICATORS:RestrictionAging')}</h2></div>
          <div className="lean-aging">{(data?.pcr?.aging || []).map((row: any) => <div key={row.bucket}><span>{row.bucket.replace('_', '–')} {t('LEAN_INDICATORS:Days')}</span><strong>{row.open}</strong><small>{row.overdue} {t('LEAN_INDICATORS:Overdue')}</small></div>)}</div>
        </section>
      </div>
    </>
  )

  const renderCnc = () => (
    <div className="lean-two-columns">
      <section className="lean-panel"><div className="lean-panel__heading"><div><h2>{t('LEAN_INDICATORS:CNCPareto')}</h2><p>{t('LEAN_INDICATORS:OnePrimaryCause')}</p></div><strong>{formatPercent(data?.cnc?.coverage?.value)}</strong></div>
        {!data?.cnc?.pareto?.length ? <EmptyState text={t('LEAN_INDICATORS:NoFailures')} /> : <><ResponsiveContainer width="100%" height={300}><ComposedChart data={data.cnc.pareto} layout="vertical" margin={{ left: 20, right: 24 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} /><XAxis type="number" /><YAxis type="category" dataKey="label" width={120} /><Tooltip /><Bar dataKey="count" name={t('LEAN_INDICATORS:Failures')} fill="#ff6720">{data.cnc.pareto.map((row: any, index: number) => <Cell key={row.code} fill={colors[index % colors.length]} />)}</Bar><Line dataKey="cumulativePercent" name="% acumulado" stroke="#663399" /></ComposedChart></ResponsiveContainer><div className="lean-pareto-links">{data.cnc.pareto.map((row: any) => <button key={row.code} onClick={() => openDrilldown({ metric: 'cnc', bucket: row.code }, row.label)}>{row.label} <strong>{row.count}</strong></button>)}</div></>}
      </section>
      <section className="lean-panel"><div className="lean-panel__heading"><h2>{t('LEAN_INDICATORS:CorrectiveActions')}</h2><strong>{data?.cnc?.actions?.overdue || 0} {t('LEAN_INDICATORS:Overdue')}</strong></div>
        <div className="lean-status-list">{(data?.cnc?.actions?.byStatus || []).map((row: any) => <div key={row.status}><span>{row.status.replace(/_/g, ' ')}</span><strong>{row.count}</strong></div>)}</div>
        <div className="lean-lead-time"><span>{t('LEAN_INDICATORS:MedianAnalysisTime')}</span><strong>{data?.cnc?.analysisLeadTimeHours?.median == null ? '—' : `${data.cnc.analysisLeadTimeHours.median.toFixed(1)} h`}</strong></div>
      </section>
    </div>
  )

  const renderQuality = () => (
    <section className="lean-panel"><div className="lean-panel__heading"><div><h2>{t('LEAN_INDICATORS:DataQuality')}</h2><p>{t('LEAN_INDICATORS:DataQualityDescription')}</p></div><span className={`lean-quality-badge ${quality.length ? 'has-warnings' : ''}`}>{quality.length}</span></div>
      {!quality.length ? <EmptyState text={t('LEAN_INDICATORS:NoQualityIssues')} /> : <div className="lean-quality-list">{quality.map((row: any, index: number) => <button key={`${row.code}-${index}`} onClick={() => openDrilldown({ metric: 'quality', bucket: row.code }, row.code)}><span className={`severity severity--${row.severity || 'warning'}`}>{row.severity || 'warning'}</span><code>{row.code}</code><strong>{row.count}</strong></button>)}</div>}
    </section>
  )

  const dashboard = (
    <div className="lean-dashboard">
      {isDemo && <div className="lean-demo-banner"><strong>{t('LEAN_INDICATORS:Demo')}</strong><span>{t('LEAN_INDICATORS:DemoDescription')}</span></div>}
          <div className="lean-toolbar">
            <nav className="lean-subtabs" aria-label={t('LEAN_INDICATORS:Title')} role="tablist">
              {sections.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={section === item}
                  className={section === item ? 'active' : ''}
                  onClick={() => selectSection(item)}
                >
                  {t(`LEAN_INDICATORS:Tab_${item}`)}
                </button>
              ))}
            </nav>
            <div className="lean-filters"><label>{t('LEAN_INDICATORS:From')}<input value={from} onChange={(event) => setFrom(event.target.value)} placeholder="2026-W01" /></label><label>{t('LEAN_INDICATORS:To')}<input value={to} onChange={(event) => setTo(event.target.value)} placeholder="2026-W52" /></label><button onClick={() => refresh()} disabled={loading}>{t('LEAN_INDICATORS:Apply')}</button></div>
          </div>
          <div className="lean-dimension-filters">
            {Object.keys(filterOptions).map((key) => (
              <LeanFilterDropdown
                key={key}
                id={`lean-filter-${key}`}
                label={t(`LEAN_INDICATORS:Filter_${key}`)}
                value={(dimensionFilters as any)[key]}
                options={(filterOptions as any)[key]}
                allLabel={t('LEAN_INDICATORS:All')}
                onChange={(value) => updateDimensionFilter(key, value)}
              />
            ))}
            <button onClick={clearFilters}>{t('LEAN_INDICATORS:ClearFilters')}</button>
          </div>
          {loading && !data && <div className="lean-loading">{t('LEAN_INDICATORS:Loading')}</div>}
          {error && <div className="lean-error" role="alert"><span>{error}</span><button onClick={() => refresh()}>{t('LEAN_INDICATORS:Retry')}</button></div>}
          {data && <main>{section === 'summary' && renderSummary()}{section === 'ppc' && renderPpc()}{section === 'pcr' && renderPcr()}{section === 'cnc' && renderCnc()}{section === 'quality' && renderQuality()}</main>}
          {renderDrilldown()}
    </div>
  )

  if (isDemo) return (
    <div className="lean-preview-shell">
      <header><div className="lean-preview-brand">PL4NNER</div><h1>{t('LEAN_INDICATORS:Title')}</h1></header>
      {dashboard}
    </div>
  )

  return (
    <ProjectLayout menuName="start" subMenuName="indicators" hideSubMenu>
      <ProjectOverviewHeader activeTab="indicators" />
      <ContentBody>
        {dashboard}
      </ContentBody>
    </ProjectLayout>
  )
}

export default LeanIndicatorsDashboardPage
